import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { AudioLines, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MfaStep } from "@/pages/Login";

/* /reset#token=… — one-time link issued by an owner from Administration. */
export default function ResetPassword() {
  const { user, refreshUser } = useAuth();
  const [token] = useState(() => new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token") || "");
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [error, setError] = useState(""); const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);
  useEffect(() => {
    if (!token) { setInfo({ ok: false, error: "This link is missing its code." }); return; }
    api.get(`/auth/reset/${encodeURIComponent(token)}`).then((r) => setInfo({ ok: true, ...r.data })).catch((e) => setInfo({ ok: false, error: formatApiError(e) }));
  }, [token]);
  if (user) return <Navigate to="/albums?sort=recent" replace />;
  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (pw !== pw2) { setError("The passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset", { token, password: pw });
      if (data.mfa_required) setMfa(data.mfa_token); else await refreshUser();
    } catch (err) { setError(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF9F6] px-6">
      <div className="w-full max-w-sm animate-rise" data-testid="reset-page">
        <div className="mb-10 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-zinc-900 text-white"><AudioLines size={18} /></div>
          <span className="font-heading text-2xl font-bold tracking-tight">Resonance Harbor</span>
        </div>
        {mfa ? <MfaStep mfaToken={mfa} onDone={refreshUser} /> : !info ? <Loader2 className="animate-spin text-zinc-400" /> : !info.ok ? (
          <>
            <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="reset-invalid">This link doesn't work any more</h1>
            <p className="mt-3 text-sm text-zinc-500">{info.error.replace(/\.?$/, ".")} Ask the library owner for a new one.</p>
            <Link to="/login" className="mt-8 inline-block text-sm underline-offset-4 hover:underline" data-testid="reset-back">Back to sign in</Link>
          </>
        ) : (
          <>
            <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl" data-testid="reset-title">Choose a new password</h1>
            <p className="mt-3 text-sm text-zinc-500">For <span className="font-mono">{info.email}</span>. Every device signed in to this account will be signed out.</p>
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
              <div className="space-y-2"><Label htmlFor="pw">New password</Label><Input id="pw" type="password" required minLength={8} autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 bg-white" data-testid="reset-password" /></div>
              <div className="space-y-2"><Label htmlFor="pw2">Repeat it</Label><Input id="pw2" type="password" required minLength={8} autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="h-11 bg-white" data-testid="reset-password2" /></div>
              {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="reset-error">{error}</p>}
              <button type="submit" disabled={busy || pw.length < 8} className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-zinc-900 text-sm font-medium text-white disabled:opacity-60" data-testid="reset-submit">{busy && <Loader2 size={16} className="animate-spin" />}Set password & sign in</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
